import { gql } from "@apollo/client"
import { RestrictiveCovenantsSectionFragment } from "~/shared/components/FormSections/RestrictiveCovenantsSection/fragment"
import {
  ContractedTerminationNoticeFragment,
  ResignationNoticeFragment,
} from "~/shared/fragments/ContractedNoticeFragment"
import { RoleTypeFragment } from "~/shared/fragments/RoleTypeFragment"

const PERSONAL_INFORMATION_FRAGMENT = gql`
  fragment PersonalInformationFragment on Employment {
    coEmployee {
      pushedFields
      userPersonalInfo {
        syncedFields
        pushedFields
      }
    }
    company {
      id
      name
    }
    countryCode
    subdivisionCode
    hiring {
      id
    }
    id
    employmentComplianceCheck
    employmentComplianceDetails
    personalInfo {
      agreementsFields
      birthSex
      dateOfBirth
      email
      firstName
      fullLegalName
      lastName
      middleName
      nationality
      personalPhoneNumber
      preferredName
      preferredPronoun
      workEmailAddress
      syncedFields
    }
    workPermitRequired
  }
`

const POSITION_INFORMATION_FRAGMENT = gql`
  fragment PositionInformationFragment on Employment {
    cbaLevel
    employmentType
    initialStartDate
    jobDescription
    jobDescriptionTranslation
    jobRoleTranslation
    minimumQualification
    dailyResponsibility
    primaryDuty
    decisionMakingAuthority
    managerName
    managerRole
    newHireStatus
    partTimeDaysPerWeek
    partTimeHoursPerDay
    role
    isPeoTeamMember
    seniorityLevel
    syncedFields
    pushedFields
    workFocusedWithinCountryOfResidence
    workingHours
    fullTimeHoursPerWeek
    roleType {
      ...RoleTypeFragment
    }
  }

  ${RoleTypeFragment}
`

const CONTRACT_TERMS_FRAGMENT = gql`
  fragment ContractTermsFragment on Employment {
    contractedSeveranceRequired
    endDate
    probationEnd
    probationPeriodType
    probationPeriodValue
    startDate
    ...ResignationNoticeFragment
    ...ContractedTerminationNoticeFragment
  }
  ${ResignationNoticeFragment}
  ${ContractedTerminationNoticeFragment}
`

const RESTRICTIVE_COVENANTS_FRAGMENT = gql`
  fragment RestrictiveCovenantsFragment on Employment {
    restrictiveCovenants {
      ...RestrictiveCovenantsSectionFragment
    }
  }
  ${RestrictiveCovenantsSectionFragment}
`

const INTERNAL_USE_FRAGMENT = gql`
  fragment InternalUseFragment on Employment {
    assignedStaff {
      avatarUrl
      id
      name
      email
    }
    createdAt
    createdBy {
      avatarUrl
      id
      name
    }
    employerOfRecord {
      id
      name
    }
    invitation {
      usedTokenAt
    }
    privateNotes
    state
    internalMigration
    internalBulkImport
    hiringSuccessLead {
      avatarUrl
      id
      name
      email
    }
    offboardingSpecialist {
      avatarUrl
      id
      name
      email
    }
    payrollSpecialist {
      avatarUrl
      id
      name
      email
    }
  }
`

export const GENERAL_STAFF_EMPLOYMENT_PROVIDER_INFORMATION_ENGAGEMENT_FRAGMENT = gql`
  fragment GeneralStaffEmploymentProviderInformationEngagementFragment on Engagement {
    vendorExternalId
    isPayrollTeamMember
    assignedStaff {
      avatarUrl
      id
      name
    }
    company {
      id
    }
    employment {
      ...ContractTermsFragment
      ...InternalUseFragment
      ...PersonalInformationFragment
      ...PositionInformationFragment
      ...RestrictiveCovenantsFragment
      salaryAmount
      salaryCurrencyCode
      state
      isUsaExemptEmployee
      isPayrollTeamMember
      hiring {
        id
        onboardingForm {
          id
        }
        hiringProcess {
          hiringFormCompleted
          exemptionFormCompleted
        }
      }
      workSchedules {
        startTime
        endTime
        day
      }
    }
    id
    teamMember {
      id
      name
      latestKycVerification {
        status
        id
        reason
        latestWebhookData
        updatedAt
      }
    }
    state
    dependents {
      collection {
        id
        fieldsWithValues
      }
    }
  }

  ${CONTRACT_TERMS_FRAGMENT}
  ${INTERNAL_USE_FRAGMENT}
  ${PERSONAL_INFORMATION_FRAGMENT}
  ${POSITION_INFORMATION_FRAGMENT}
  ${RESTRICTIVE_COVENANTS_FRAGMENT}
`
